<template>
  <div class="relative w-full h-full py-6 px-6 mt-24 bg-light-screen-background dark:bg-dark-screen-background flex flex-col">
    <!-- Header Stats (Fixed) -->
    <div class="flex-shrink-0">
      <div v-if="playbook.stats" class="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="(value, key) in filteredStats"
          :key="key"
          class="p-4 bg-white dark:bg-dark-box-background rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
          <div class="flex items-center justify-between">
            <div class="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
              {{ $t(key) }}
            </div>
            <div :class="getStatColor(key)" class="text-2xl font-semibold">
              {{ value }}
            </div>
          </div>
        </div>
      </div>

      <h3 class="text-lg font-medium text-gray-900 dark:text-dark-body-text mb-2">
        {{ $t("Execution Flow") }}
      </h3>
    </div>

    <!-- Execution Logs List (Scrollable) -->
    <div class="flex-grow overflow-auto timeline-fade">
      <TransitionGroup name="list" tag="div" class="relative space-y-4 pb-12" appear>
        <!-- Timeline Line -->
        <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-800" />
        <div
          v-for="(execution_log, index) in executions_logs"
          :key="execution_log.id || execution_log.playbook_execution_id"
          class="relative pl-12 pb-8 group"
          :class="{ 'opacity-75': index > 0 && execution_log.playbook_execution_id === executions_logs[index - 1].playbook_execution_id }">
          <!-- Timeline Node -->
          <div
            class="absolute left-1.5 top-1.5 w-5 h-5 rounded-full border-4 border-white dark:border-gray-800 z-10"
            :class="execution_log.action_type === 'stepExecutionStart' ? 'bg-green-500 dark:bg-green-600' : 'bg-blue-500 dark:bg-blue-600'">
            <span class="sr-only">{{ execution_log.action_type === "stepExecutionStart" ? $t("Started") : $t("Completed") }}</span>
          </div>

          <!-- Timeline Content -->
          <div
            class="bg-white dark:bg-dark-box-background shadow-sm hover:shadow-md transition-all duration-200 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800">
            <!-- Header Section -->
            <div class="px-5 py-4 bg-gray-50 dark:bg-gray-800/30 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div
                  class="flex items-center rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200"
                  :class="getStatusClasses(execution_log)">
                  {{ execution_log.action_type === "stepExecutionStart" ? $t("Started") : $t("Completed") }}
                </div>
                <h4 class="font-medium text-gray-800 dark:text-gray-200">
                  {{ execution_log.label || getStepName(execution_log) }}
                </h4>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-700 dark:text-dark-body-text px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                  {{ execution_log.step_type === "trigger" ? $t("Trigger") : $t("Action") }}
                </span>
                <div v-if="calculateDuration(execution_log)" class="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Timer class="w-4 h-4 mr-1" />
                  {{ calculateDuration(execution_log) }}
                </div>
              </div>
            </div>

            <!-- Details Grid -->
            <div class="p-5">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Left Column -->
                <div class="space-y-4">
                  <div class="flex flex-col">
                    <span class="text-xs text-common-grey mb-1">{{ $t("User") }}</span>
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <User class="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <span class="text-sm font-medium text-gray-900 dark:text-dark-body-text truncate">
                        {{ getUserName(execution_log) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="space-y-4">
                  <div class="flex flex-col">
                    <span class="text-xs text-common-grey mb-1">{{ $t("Execution ID") }}</span>
                    <div class="flex items-center group">
                      <span class="text-sm font-medium text-gray-900 dark:text-dark-body-text font-mono truncate">
                        {{ getShortExecutionId(execution_log) }}
                      </span>
                      <button
                        class="ml-2 p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity duration-200"
                        :title="$t('Copy to clipboard')"
                        @click="copyToClipboard(execution_log)">
                        <Clipboard class="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div class="flex flex-col">
                    <span class="text-xs text-common-grey mb-1">{{ $t("Associated Object") }}</span>
                    <div v-if="getObjectName(execution_log)" class="flex items-center space-x-2">
                      <component :is="getObjectIcon(getObjectType(execution_log))" class="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <a
                        :href="getObjectLink(execution_log)"
                        class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 truncate transition-colors duration-200">
                        {{ getObjectName(execution_log) }}
                      </a>
                      <span class="text-xs text-gray-500 dark:text-gray-400 capitalize"> ({{ getObjectType(execution_log) }}) </span>
                    </div>
                    <span v-else class="text-sm text-gray-500 dark:text-gray-400">{{ $t("None") }}</span>
                  </div>
                </div>
              </div>

              <!-- Error Section -->
              <TransitionExpand>
                <div v-if="hasErrors(execution_log)" class="mt-6">
                  <div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div class="flex items-start">
                      <AlertCircle class="h-5 w-5 text-red-400 dark:text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
                          {{ $t("Execution Errors") }}
                        </h4>
                        <ul class="space-y-2">
                          <li v-for="(error, index) in execution_log?.errors" :key="index" class="text-sm text-red-700 dark:text-red-200">
                            {{ error }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionExpand>

              <!-- Expand Details Button -->
              <button
                class="mt-6 flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
                @click="toggleExecutionDetails(execution_log)">
                <ChevronRight
                  class="h-4 w-4 transition-transform duration-200"
                  :class="{ 'rotate-90': isExecutionExpanded(execution_log) }" />
                {{ isExecutionExpanded(execution_log) ? $t("Hide details") : $t("Show details") }}
              </button>

              <!-- Expanded JSON Details -->
              <TransitionExpand>
                <div v-if="isExecutionExpanded(execution_log)" class="mt-4">
                  <pre
                    class="text-xs font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-auto max-h-96 border border-gray-200 dark:border-gray-700">{{ formatJson(execution_log) }}</pre>
                </div>
              </TransitionExpand>
            </div>
          </div>
        </div>
      </TransitionGroup>

      <!-- Empty State -->
      <TransitionFade>
        <div
          v-if="executions_logs.length === 0"
          class="flex flex-col items-center justify-center p-12 bg-white dark:bg-dark-box-background shadow-sm rounded-lg text-center">
          <Info class="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-dark-body-text mb-2">
            {{ $t("No execution logs") }}
          </h3>
          <p class="text-sm text-common-grey max-w-md">
            {{ $t("No execution logs have been recorded for") }} "{{ playbook.name }}".
          </p>
        </div>
      </TransitionFade>
    </div>
  </div>
</template>

<script>
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import {
  Info,
  Clipboard,
  ChevronRight,
  AlertCircle,
  User,
  Clock,
  Link,
  CheckSquare,
  Flag,
  MessageSquare,
  FileText,
  Table,
  Timer
} from "lucide-vue-next"
import { enrichFlowChartWithNames } from "./PlaybookEditor/forms/schema.js"
import TransitionExpand from "@/components/transitions/TransitionExpand.vue"
import TransitionFade from "@/components/transitions/TransitionFade.vue"

export default {
  name: "PlaybookExecutions",

  components: {
    Info,
    Clipboard,
    ChevronRight,
    AlertCircle,
    User,
    Clock,
    Link,
    CheckSquare,
    Flag,
    MessageSquare,
    FileText,
    Table,
    Timer,
    TransitionExpand,
    TransitionFade
  },

  props: {
    playbook: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      executions_logs: [],
      expandedExecutions: new Set()
    }
  },

  computed: {
    filteredStats() {
      const { stats } = this.playbook
      if (!stats) {
        return {}
      }

      const relevantStats = ["executions", "running", "succeeded", "failed"]
      return Object.fromEntries(Object.entries(stats).filter(([key]) => relevantStats.includes(key)))
    }
  },

  created() {
    this.$store.dispatch("realtime/joinStream", this.playbook.id, { root: true })

    this.$emitter.on(ALL_EMITTERS.UPDATE_PLAYBOOK_EXECUTION_STEP, (data) => {
      if (data.playbook_id == this.playbook.id) {
        // Add each log entry individually
        this.executions_logs.unshift(data)

        // Sort logs by timestamp (newest first)
        this.executions_logs.sort((a, b) => {
          return b.timestamp - a.timestamp
        })
      }
    })
  },

  beforeUnmount() {
    this.$store.dispatch("realtime/leaveStream", this.playbook.id, { root: true })
    this.$emitter.off(ALL_EMITTERS.UPDATE_PLAYBOOK_EXECUTION_STEP)
  },

  methods: {
    getStepName(execution) {
      try {
        if (execution.steps) {
          // Get the latest step (the one with the highest step number)
          const sortedSteps = Object.values(execution.steps).sort((a, b) => parseInt(a.step_name) - parseInt(b.step_name))

          if (sortedSteps.length > 0) {
            const latestStep = sortedSteps[sortedSteps.length - 1]
            return latestStep.label || `Step ${latestStep.step_name}`
          }
        }

        // Support for new format with direct step_name and label
        if (execution.step_name && execution.label) {
          return execution.label || `Step ${execution.step_name}`
        }

        return this.fallbackGetStepName(execution)
      } catch (error) {
        console.error("Error getting step name:", error)
        return this.fallbackGetStepName(execution)
      }
    },

    fallbackGetStepName(execution) {
      const enrichedChart = enrichFlowChartWithNames(this.playbook.flowchart)
      const currentStep = execution.context.current_step
      return enrichedChart.main.steps[currentStep]?.name || `Step ${currentStep}`
    },

    getObjectType(execution) {
      const triggerTypes = ["task_trigger", "plan_trigger", "milestone_trigger", "interaction_trigger", "goal_trigger", "record_trigger"]

      for (const type of triggerTypes) {
        if (execution?.arguments?.[type] || execution?.context?.[type]) {
          return type.split("_")[0]
        }
      }
      return null
    },

    getObjectName(execution) {
      const type = this.getObjectType(execution)
      if (!type) {
        return null
      }

      const trigger = `${type}_trigger`
      return execution?.arguments?.[trigger]?.name || execution?.context?.[trigger]?.name || null
    },

    getObjectLink(execution) {
      const type = this.getObjectType(execution)
      if (!type) {
        return "#"
      }

      const trigger = `${type}_trigger`
      return execution?.arguments?.[trigger]?.link || execution?.context?.[trigger]?.link || "#"
    },

    getObjectIcon(type) {
      const icons = {
        task: CheckSquare,
        goal: Flag,
        interaction: MessageSquare,
        plan: FileText,
        record: Table,
        milestone: Flag
      }
      return icons[type] || Link
    },

    getStatColor(key) {
      const colors = {
        executions: "text-blue-600 dark:text-blue-400",
        running: "text-yellow-600 dark:text-yellow-400",
        succeeded: "text-green-600 dark:text-green-400",
        failed: "text-red-600 dark:text-red-400"
      }
      return colors[key] || "text-gray-900 dark:text-gray-100"
    },

    getStatusClasses(execution) {
      if (execution.action_type === "stepExecutionEnd") {
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
      } else {
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
      }
    },

    getUserName(execution) {
      // For the new format, we don't have user info yet, so default to current user
      if (execution.playbook_execution_id && !execution.user) {
        return this.$store.state.user?.full_name || this.$t("Unknown User")
      }

      return this.$utils.getUserById(execution.user).full_name || this.$t("Unknown User")
    },

    getShortExecutionId(execution) {
      let id
      if (typeof execution === "object") {
        // Support for both formats
        id = execution.id || execution.playbook_execution_id
      } else {
        id = execution
      }

      if (!id) {
        return this.$t("Unknown")
      }
      return id.length > 12 ? `${id.substring(0, 8)}...` : id
    },

    hasErrors(execution) {
      return execution?.errors?.length > 0
    },

    formatJson(data) {
      return JSON.stringify(data, null, 2)
    },

    copyToClipboard(execution) {
      const text = typeof execution === "object" ? execution.id || execution.playbook_execution_id : execution

      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast.success(this.$t("Copied to clipboard"))
        })
        .catch((err) => {
          console.error("Could not copy text: ", err)
          this.$toast.error(this.$t("Failed to copy"))
        })
    },

    toggleExecutionDetails(execution) {
      const uniqueId = this.getUniqueExecutionLogId(execution)

      if (this.expandedExecutions.has(uniqueId)) {
        this.expandedExecutions.delete(uniqueId)
      } else {
        this.expandedExecutions.add(uniqueId)
      }
    },

    isExecutionExpanded(execution) {
      const uniqueId = this.getUniqueExecutionLogId(execution)
      return this.expandedExecutions.has(uniqueId)
    },

    getUniqueExecutionLogId(execution) {
      const id = execution.id || execution.playbook_execution_id
      const actionType = execution.action_type || ""
      const stepId = execution.step_id || ""

      return `${id}-${actionType}-${stepId}-${execution.timestamp || ""}`
    },

    calculateDuration(execution) {
      // Handle new format with direct duration field
      if (execution.duration !== undefined) {
        const durationSec = execution.duration

        if (durationSec < 1) {
          return `${Math.round(durationSec * 1000)}ms`
        } else if (durationSec < 60) {
          return `${durationSec.toFixed(1)}s`
        } else if (durationSec < 3600) {
          const minutes = Math.floor(durationSec / 60)
          const seconds = Math.round(durationSec % 60)
          return `${minutes}m ${seconds}s`
        } else {
          const hours = Math.floor(durationSec / 3600)
          const minutes = Math.floor((durationSec % 3600) / 60)
          return `${hours}h ${minutes}m`
        }
      }
      return ""
    }
  }
}
</script>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.list-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

/* Timeline fade effect at the bottom */
.timeline-fade::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, var(--bg-color) 100%);
  pointer-events: none;
}

.dark .timeline-fade::after {
  --bg-color: rgba(30, 30, 30, 1);
}

.light .timeline-fade::after {
  --bg-color: rgba(255, 255, 255, 1);
}
</style>
./PlaybookEditor/forms/schema.js